import { Observable, catchError, map, of } from 'rxjs';
import { HttpClientInterface } from '@itk/http-client-interface';
import {
  QuestionnaireResponse,
  QuestionnaireRequest,
  SearchConditionRequest,
  UnderwritingItem,
  questionnaireToUnderwritingItem,
} from '../models/questionnaire.model';
import { IMaybe, maybe, none } from 'typescript-monads';

export class QuestionnaireEndpoint {
  constructor(private readonly http: HttpClientInterface) {}

  searchCondition(req: SearchConditionRequest): Observable<string[]> {
    return this.http
      .post<string[], SearchConditionRequest>({
        url: '/questionnaire/search/condition/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(() => of([])));
  }

  searchDrug(req: SearchConditionRequest): Observable<string[]> {
    return this.http
      .post<string[], SearchConditionRequest>({
        url: '/questionnaire/search/drug/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(() => of([])));
  }

  questionnaire(
    req: QuestionnaireRequest,
  ): Observable<IMaybe<UnderwritingItem>> {
    return this.http
      .post<QuestionnaireResponse, QuestionnaireRequest>({
        url: '/questionnaire/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map((x) =>
          maybe<UnderwritingItem>(
            x ? questionnaireToUnderwritingItem(x) : null,
          ),
        ),
        catchError(() => of(none<UnderwritingItem>())),
      );
  }

  questionnaireLoad(schema: object): Observable<{ quote_id: string, data: object }> {
    return this.http
      .post<{ quote_id: string, data: object }, object>({
        url: '/questionnaire/load/',
        body: schema,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(() => of({ quote_id: '', data: {} })));
  }

  questionnaireLoadQuoteId(quoteId: string): Observable<{ data: object }> {
    return this.http
      .get<{ data: object }>({
        url: `/questionnaire/load/${quoteId ?? ''}/`,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(catchError(() => of({ data: {} })));
  }

  questionnaireTraversal(
    req: QuestionnaireRequest,
  ): Observable<IMaybe<UnderwritingItem>> {
    return this.http
      .post<QuestionnaireResponse, QuestionnaireRequest>({
        url: '/questionnaire/traversal/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map((x) => maybe<UnderwritingItem>(questionnaireToUnderwritingItem(x))),
        catchError(() => of(none<UnderwritingItem>())),
      );
  }

  questionnaireTraversalGet(
    sessionId: string,
  ): Observable<IMaybe<UnderwritingItem>> {
    return this.http
      .get<QuestionnaireResponse>({
        url: `/questionnaire/traversal/${sessionId ?? ''}/`,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map((x) => maybe<UnderwritingItem>(questionnaireToUnderwritingItem(x))),
        catchError(() => of(none<UnderwritingItem>())),
      );
  }

  questionnaireTraversalPrevious(
    req: QuestionnaireRequest,
  ): Observable<IMaybe<UnderwritingItem>> {
    return this.http
      .post<QuestionnaireResponse, QuestionnaireRequest>({
        url: '/questionnaire/traversal/prev/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map((x) => maybe<UnderwritingItem>(questionnaireToUnderwritingItem(x))),
        catchError(() => of(none<UnderwritingItem>())),
      );
  }

  questionnaireTraversalEdit(
    req: QuestionnaireRequest,
  ): Observable<IMaybe<UnderwritingItem>> {
    return this.http
      .post<QuestionnaireResponse, QuestionnaireRequest>({
        url: '/questionnaire/traversal/edit/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map((x) => maybe<UnderwritingItem>(questionnaireToUnderwritingItem(x))),
        catchError(() => of(none<UnderwritingItem>())),
      );
  }

  questionnaireTraversalAnswer(
    req: QuestionnaireRequest,
  ): Observable<IMaybe<UnderwritingItem>> {
    return this.http
      .post<QuestionnaireResponse, QuestionnaireRequest>({
        url: '/questionnaire/traversal/answer/',
        body: req,
        headers: { 'Content-Type': 'application/json' },
      })
      .pipe(
        map((x) => maybe<UnderwritingItem>(questionnaireToUnderwritingItem(x))),
        catchError(() => of(none<UnderwritingItem>())),
      );
  }
}
